import { type Ref } from 'vue'

const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="button"], [tabindex]:not([tabindex="-1"])'

export interface UseFocusTrapOptions {
  /** When provided, Tab handling is skipped while this ref is `false`. */
  active?: Ref<boolean>
}

export interface UseFocusTrap {
  /** Currently focusable, visible elements inside the container, in DOM order. */
  focusables: () => HTMLElement[]
  /** Focus the first tabbable element, falling back to the container itself. */
  focusFirstTabbable: () => void
  /** Start trapping Tab/Shift-Tab (adds the keydown listener). */
  activate: () => void
  /** Stop trapping (removes the keydown listener). */
  deactivate: () => void
  /** True while this trap is the most recently activated one - the layer the user is actually in. */
  isTopmost: () => boolean
}

/** Every active trap, oldest first; only the top handles keys so stacked modals do not fight over Tab. */
const activeTraps: symbol[] = []

export function useFocusTrap(
  containerRef: Ref<HTMLElement | null>,
  options: UseFocusTrapOptions = {},
): UseFocusTrap {
  function focusables(): HTMLElement[] {
    const container = containerRef.value
    if (!container) return []
    // Not `offsetParent`: it is always null for position:fixed elements, which would skip overlay focusables.
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (element) => element.getClientRects().length > 0,
    )
  }

  function focusFirstTabbable(): void {
    ;(focusables()[0] ?? containerRef.value)?.focus()
  }

  const token = Symbol('focus-trap')

  function isTopmost(): boolean {
    return activeTraps[activeTraps.length - 1] === token
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return
    if (!isTopmost()) return
    if (options.active && !options.active.value) return
    const container = containerRef.value
    if (!container) return

    const elements = focusables()
    if (elements.length === 0) {
      event.preventDefault()
      container.focus()
      return
    }
    const first = elements[0]!
    const last = elements[elements.length - 1]!
    const activeElement = document.activeElement as HTMLElement | null
    if (!container.contains(activeElement)) {
      event.preventDefault()
      first.focus()
      return
    }
    if (event.shiftKey && activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  function activate(): void {
    if (typeof document === 'undefined') return
    if (!activeTraps.includes(token)) activeTraps.push(token)
    document.addEventListener('keydown', onKeydown)
  }

  function deactivate(): void {
    if (typeof document === 'undefined') return
    const index = activeTraps.indexOf(token)
    if (index !== -1) activeTraps.splice(index, 1)
    document.removeEventListener('keydown', onKeydown)
  }

  return { focusables, focusFirstTabbable, activate, deactivate, isTopmost }
}
